import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/react';
import BEM from 'common/bem';
import downloadFile from 'common/utils/downloadFile';
import type {
  ArchivingAuditLogEntry,
  ArchivingAuditLogListRequest,
  ArchivingAuditLogListResponse,
  ArchivingAuditOutcome,
} from 'js-sdk/src/apis/ArchivingAPI';

const classes = BEM.with('AuditLog');

// A filtered page can come back short, or even empty, while a nextToken is still present (non-actor
// filters are applied after the key lookup server-side). Keep fetching on the user's behalf for a
// few pages when a page comes back empty, so "No events" is never shown while more may follow.
const MAX_AUTO_PAGES = 5;
const DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'search_performed', label: 'Search performed' },
  { value: 'messages_returned', label: 'Messages returned' },
  { value: 'conversation_metadata_returned', label: 'Conversation details returned' },
  { value: 'attachment_returned', label: 'Attachment returned' },
  { value: 'export_requested', label: 'Export requested' },
  { value: 'export_completed', label: 'Export completed' },
  { value: 'export_failed', label: 'Export failed' },
  { value: 'export_downloaded', label: 'Export downloaded' },
  { value: 'export_history_listed', label: 'Export history listed' },
  { value: 'saved_search_created', label: 'Saved search created' },
  { value: 'saved_search_listed', label: 'Saved searches listed' },
  { value: 'saved_search_deleted', label: 'Saved search deleted' },
  { value: 'audit_log_read', label: 'Audit log read' },
  { value: 'access_denied', label: 'Access denied' },
];

const EVENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  EVENT_TYPE_OPTIONS.map(({ value, label }) => [value, label])
);

// Outcomes are always shown as a symbol plus a word, never color alone.
const OUTCOME_DISPLAY: Record<ArchivingAuditOutcome, { symbol: string; label: string }> = {
  success: { symbol: '✓', label: 'Success' },
  failure: { symbol: '✕', label: 'Failed' },
  denied: { symbol: '⊘', label: 'Denied' },
};

type AuditLogClient = {
  archiving: {
    getAuditLog: (
      organizationId: string,
      options?: Omit<ArchivingAuditLogListRequest, 'orgId'>
    ) => Promise<ArchivingAuditLogListResponse>;
  };
};

type AuditLogProps = {
  client: AuditLogClient | null;
  currentOrganizationId: string;
};

type Filters = {
  eventType: string;
  outcome: '' | ArchivingAuditOutcome;
  actor: string;
  resourceId: string;
  fromDate: string;
  toDate: string;
};

const EMPTY_FILTERS: Filters = {
  eventType: '',
  outcome: '',
  actor: '',
  resourceId: '',
  fromDate: '',
  toDate: '',
};

export function eventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] || eventType;
}

function outcomeDisplay(outcome: string): { symbol: string; label: string } {
  return OUTCOME_DISPLAY[outcome as ArchivingAuditOutcome] || { symbol: '', label: outcome };
}

/** Turns the filter form into getAuditLog options, sending only filters that were filled in. */
function toRequestOptions(filters: Filters): Omit<ArchivingAuditLogListRequest, 'orgId'> {
  const options: Omit<ArchivingAuditLogListRequest, 'orgId'> = {};
  if (filters.eventType) options.eventType = filters.eventType;
  if (filters.outcome) options.outcome = filters.outcome;
  if (filters.actor.trim()) options.actorAccountToken = filters.actor.trim();
  if (filters.resourceId.trim()) options.resourceId = filters.resourceId.trim();
  if (filters.fromDate) {
    options.from = moment(filters.fromDate, DATE_INPUT_FORMAT).startOf('day').toISOString();
  }
  if (filters.toDate) {
    options.to = moment(filters.toDate, DATE_INPUT_FORMAT).endOf('day').toISOString();
  }
  return options;
}

function isPermissionDenied(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'permission-denied';
}

function describeFilters(entry: ArchivingAuditLogEntry): string[] {
  const filters = entry.filters;
  if (!filters) return [];
  const lines: string[] = [];
  if (filters.startDate || filters.endDate) {
    lines.push(`Date range: ${filters.startDate || '…'} to ${filters.endDate || '…'}`);
  }
  if (filters.senderAccountIds?.length) {
    lines.push(`Senders: ${filters.senderAccountIds.join(', ')}`);
  }
  if (filters.recipientAccountIds?.length) {
    lines.push(`Recipients: ${filters.recipientAccountIds.join(', ')}`);
  }
  if (filters.messageType) lines.push(`Message type: ${filters.messageType}`);
  if (filters.hasAttachment) lines.push('Has attachment');
  if (filters.hasPatientReference) lines.push('Patient reference supplied');
  if (filters.auditLogActor) lines.push(`Log filtered by actor: ${filters.auditLogActor}`);
  if (filters.auditLogEventType) lines.push(`Log filtered by event: ${filters.auditLogEventType}`);
  if (filters.auditLogOutcome) lines.push(`Log filtered by outcome: ${filters.auditLogOutcome}`);
  if (filters.auditLogResourceId) lines.push(`Log filtered by ID: ${filters.auditLogResourceId}`);
  return lines;
}

// An event can carry thousands of message IDs, so they are not listed on screen: the expanded row
// shows the count and offers the full list as a copy or a .txt download (one ID per line).
function MessageIdActions({ entry }: { entry: ArchivingAuditLogEntry }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const idsText = entry.messageIds.join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(idsText);
      setCopyState('copied');
    } catch (error) {
      Sentry.logger.warn('AuditLog: copying message IDs failed', { eventId: entry.eventId });
      setCopyState('failed');
    }
  };

  const download = () =>
    downloadFile(`audit-${entry.eventId}-message-ids.txt`, 'text/plain', idsText);

  return (
    <div className={classes('details-section')}>
      <h4 className={classes('details-heading')}>
        Message IDs ({entry.messageIds.length.toLocaleString()})
      </h4>
      <div className={classes('id-actions')}>
        <button
          className={classes('load-more-button')}
          data-test-id={`audit-log-copy-ids-${entry.eventId}`}
          onClick={copy}
          type="button"
        >
          Copy IDs
        </button>
        <button
          className={classes('load-more-button')}
          data-test-id={`audit-log-download-ids-${entry.eventId}`}
          onClick={download}
          type="button"
        >
          Download .txt
        </button>
        <span aria-live="polite" className={classes('details-note')}>
          {copyState === 'copied' && 'Copied'}
          {copyState === 'failed' && 'Copy failed — use Download instead'}
        </span>
      </div>
    </div>
  );
}

function EventDetails({ entry }: { entry: ArchivingAuditLogEntry }) {
  const filterLines = describeFilters(entry);
  const fields: [string, string | number | null | undefined][] = [
    ['Actor', entry.actorAccountToken],
    ['Roles', entry.actorRoles.length > 0 ? entry.actorRoles.join(', ') : null],
    ['HTTP status', entry.httpStatus],
    ['Result count', entry.resultCount],
    ['Query length', entry.queryLength],
    ['Conversation', entry.conversationId],
    ['Export job', entry.jobId],
    ['Saved search', entry.searchId],
    ['Attachment', entry.attachmentId],
    ['Request ID', entry.requestId],
    ['IP address', entry.sourceIp],
    ['User agent', entry.userAgent],
  ];

  return (
    <div className={classes('details')} data-test-id={`audit-log-details-${entry.eventId}`}>
      <dl className={classes('details-list')}>
        {fields
          .filter(([, value]) => value !== null && value !== undefined && value !== '')
          .map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{String(value)}</dd>
            </React.Fragment>
          ))}
      </dl>
      {filterLines.length > 0 && (
        <div className={classes('details-section')}>
          <h4 className={classes('details-heading')}>Filters</h4>
          <ul className={classes('details-filters')}>
            {filterLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {entry.messageIds.length > 0 && <MessageIdActions entry={entry} />}
      <p className={classes('details-note')}>
        Message content is never recorded in the audit log, only IDs and counts.
      </p>
    </div>
  );
}

export default function AuditLog({ client, currentOrganizationId }: AuditLogProps) {
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [events, setEvents] = useState<ArchivingAuditLogEntry[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isForbidden, setIsForbidden] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  // Bumped on every fresh load (mount, apply, clear, refresh, org change) so a slower, earlier
  // response can never overwrite — or append to — the results of a newer one.
  const loadSeq = useRef(0);

  const fetchEvents = useCallback(
    async (filters: Filters, startToken: string | null, append: boolean) => {
      if (!client) return;

      loadSeq.current += 1;
      const seq = loadSeq.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setEvents([]);
        setNextToken(null);
        setExpandedEventId(null);
      }
      setError(null);
      setIsForbidden(false);

      try {
        const collected: ArchivingAuditLogEntry[] = [];
        let token = startToken;
        let pages = 0;
        let more = true;
        // Stop at the first page with any events; only keep going automatically over empty pages.
        while (more) {
          const response = await client.archiving.getAuditLog(currentOrganizationId, {
            ...toRequestOptions(filters),
            ...(token ? { nextToken: token } : {}),
          });
          if (seq !== loadSeq.current) return;

          collected.push(...(response?.data?.events || []));
          token = response?.data?.nextToken || null;
          pages += 1;
          more = collected.length === 0 && token !== null && pages < MAX_AUTO_PAGES;
        }

        setEvents((previous) => (append ? [...previous, ...collected] : collected));
        setNextToken(token);
      } catch (err) {
        if (seq !== loadSeq.current) return;
        if (isPermissionDenied(err)) {
          setIsForbidden(true);
        } else {
          Sentry.logger.error(Sentry.logger.fmt`[AuditLog] Failed to load audit log`, {
            error: err,
          });
          setError('Something went wrong loading the audit log. Please try again.');
        }
      } finally {
        if (seq === loadSeq.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [client, currentOrganizationId]
  );

  useEffect(() => {
    fetchEvents(applied, null, false);
    // `applied` is deliberately not a dependency: changing filters goes through onApply, which
    // fetches explicitly. This effect covers mount and org/client changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEvents]);

  function updateDraft(updates: Partial<Filters>) {
    setDraft((previous) => ({ ...previous, ...updates }));
  }

  function onApply(event: React.FormEvent) {
    event.preventDefault();
    if (draft.fromDate && draft.toDate && draft.toDate < draft.fromDate) {
      setFilterError('The end date must not be before the start date.');
      return;
    }
    setFilterError(null);
    setApplied(draft);
    fetchEvents(draft, null, false);
  }

  function onClear() {
    setFilterError(null);
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    fetchEvents(EMPTY_FILTERS, null, false);
  }

  const hasFiltersApplied = JSON.stringify(applied) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Audit Log</h2>
          <p className={classes('subtitle')}>
            Who searched, had returned, exported, and downloaded archived messages. Message content
            is never recorded here.
          </p>
        </div>
        <button
          className={classes('refresh-button')}
          data-test-id="audit-log-refresh"
          disabled={isLoading || isLoadingMore}
          onClick={() => fetchEvents(applied, null, false)}
          type="button"
        >
          Refresh
        </button>
      </div>

      <form className={classes('filters')} data-test-id="audit-log-filters" onSubmit={onApply}>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>Event</span>
          <select
            data-test-id="audit-log-filter-event-type"
            onChange={(event) => updateDraft({ eventType: event.target.value })}
            value={draft.eventType}
          >
            <option value="">All events</option>
            {EVENT_TYPE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>Outcome</span>
          <select
            data-test-id="audit-log-filter-outcome"
            onChange={(event) => updateDraft({ outcome: event.target.value as Filters['outcome'] })}
            value={draft.outcome}
          >
            <option value="">All outcomes</option>
            <option value="success">Success</option>
            <option value="failure">Failed</option>
            <option value="denied">Denied</option>
          </select>
        </label>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>User account ID</span>
          <input
            data-test-id="audit-log-filter-actor"
            onChange={(event) => updateDraft({ actor: event.target.value })}
            placeholder="Account token"
            type="text"
            value={draft.actor}
          />
        </label>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>Message, conversation or job ID</span>
          <input
            data-test-id="audit-log-filter-resource-id"
            onChange={(event) => updateDraft({ resourceId: event.target.value })}
            placeholder="ID"
            type="text"
            value={draft.resourceId}
          />
        </label>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>From</span>
          <input
            data-test-id="audit-log-filter-from"
            onChange={(event) => updateDraft({ fromDate: event.target.value })}
            type="date"
            value={draft.fromDate}
          />
        </label>
        <label className={classes('filter')}>
          <span className={classes('filter-label')}>To</span>
          <input
            data-test-id="audit-log-filter-to"
            onChange={(event) => updateDraft({ toDate: event.target.value })}
            type="date"
            value={draft.toDate}
          />
        </label>
        <div className={classes('filter-actions')}>
          <button
            className={classes('apply-button')}
            data-test-id="audit-log-apply"
            disabled={isLoading || isLoadingMore}
            type="submit"
          >
            Apply
          </button>
          <button
            className={classes('clear-button')}
            data-test-id="audit-log-clear"
            disabled={
              isLoading ||
              isLoadingMore ||
              (!hasFiltersApplied && JSON.stringify(draft) === JSON.stringify(EMPTY_FILTERS))
            }
            onClick={onClear}
            type="button"
          >
            Clear
          </button>
        </div>
      </form>

      {filterError && (
        <div className={classes('error')} data-test-id="audit-log-filter-error" role="alert">
          {filterError}
        </div>
      )}
      {error && (
        <div className={classes('error')} data-test-id="audit-log-error" role="alert">
          {error}
        </div>
      )}
      {isForbidden && (
        <div className={classes('error')} data-test-id="audit-log-forbidden" role="alert">
          You don&apos;t have access to this organization&apos;s audit log.
        </div>
      )}

      {isLoading && (
        <div className={classes('loading')} data-test-id="audit-log-loading">
          Loading audit log…
        </div>
      )}

      {!isLoading && !error && !isForbidden && events.length === 0 && (
        <div className={classes('empty-state')} data-test-id="audit-log-empty">
          {nextToken
            ? 'No events in the pages checked so far. Load more to keep looking.'
            : hasFiltersApplied
            ? 'No events match these filters.'
            : 'No audit events yet. Archiving activity will show up here.'}
          {nextToken && (
            <div>
              <button
                className={classes('load-more-button')}
                data-test-id="audit-log-load-more"
                disabled={isLoadingMore}
                onClick={() => fetchEvents(applied, nextToken, true)}
                type="button"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {!isLoading && events.length > 0 && (
        <table className={classes('table')} data-test-id="audit-log-table">
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Event</th>
              <th scope="col">User</th>
              <th scope="col">Outcome</th>
              <th scope="col">Results</th>
              <th scope="col">
                <span className={classes('visually-hidden')}>Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {events.map((entry) => {
              const isExpanded = expandedEventId === entry.eventId;
              const outcome = outcomeDisplay(entry.outcome);
              return (
                <React.Fragment key={entry.eventId}>
                  <tr
                    className={classes('row', { expanded: isExpanded })}
                    data-test-id={`audit-log-row-${entry.eventId}`}
                  >
                    <td>{moment(entry.timestamp).format('MMM D, YYYY h:mm:ss A')}</td>
                    <td>{eventTypeLabel(entry.eventType)}</td>
                    <td className={classes('actor')}>{entry.actorAccountToken}</td>
                    <td>
                      <span
                        className={classes('outcome', { [entry.outcome]: true })}
                        data-test-id={`audit-log-outcome-${entry.eventId}`}
                      >
                        <span aria-hidden="true">{outcome.symbol}</span> {outcome.label}
                      </span>
                    </td>
                    <td>{entry.resultCount ?? entry.messageIds.length}</td>
                    <td>
                      <button
                        aria-expanded={isExpanded}
                        className={classes('details-button')}
                        data-test-id={`audit-log-toggle-${entry.eventId}`}
                        onClick={() => setExpandedEventId(isExpanded ? null : entry.eventId)}
                        type="button"
                      >
                        {isExpanded ? 'Hide details' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={classes('details-row')}>
                      <td colSpan={6}>
                        <EventDetails entry={entry} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {!isLoading && events.length > 0 && (
        <div className={classes('footer')}>
          <span className={classes('count')} data-test-id="audit-log-count">
            Showing {events.length} event{events.length === 1 ? '' : 's'}
            {nextToken ? '' : ' — end of log'}
          </span>
          {nextToken && (
            <button
              className={classes('load-more-button')}
              data-test-id="audit-log-load-more"
              disabled={isLoadingMore}
              onClick={() => fetchEvents(applied, nextToken, true)}
              type="button"
            >
              {isLoadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
